<template>
  <div class="supplier-signup">
    <div class="supplier-signup__page">
      <div class="hero">
     <span class="hero__eyebrow">Connect. Discover. Collaborate.</span>
<h1 class="hero__title">Find the right connections for your business.</h1>
        <p class="hero__subtitle">
          Complete your profile once and connect with the partners who need exactly what you
          offer ,  from raw ingredients, to finished goods, to the services that keep it all moving.
        </p>
        <ul class="hero__points">
          <li><span class="hero__check">✓</span> Verified partner network</li>
          <li><span class="hero__check">✓</span> One onboarding, every opportunity</li>
          <li><span class="hero__check">✓</span> Built for food &amp; manufacturing supply chains</li>
        </ul>
      </div>

      <div class="supplier-signup__container">
        <!-- ============ Business type selector ============ -->
        <div class="section-block section-block--types">
          <div class="section-head">
            <span class="section-head__icon">🧭</span>
            <div>
              <h4 class="section-head__title">What best describes you?</h4>
              <p class="section-head__desc">Select all that apply — this shapes the rest of the form.</p>
            </div>
          </div>

          <div class="type-select">
            <label v-for="opt in businessTypeOptions" :key="opt.value" class="type-card"
              :class="{ 'is-active': businessTypes.includes(opt.value) }">
              <input type="checkbox" :value="opt.value" v-model="businessTypes" class="type-card__input" name="types[]"
                :id="`type_${opt.value}`" :ref="`type_${opt.value}`" />
              <span class="type-card__icon">{{ opt.icon }}</span>
              <span class="type-card__label">{{ opt.label }}</span>
              <span class="type-card__desc">{{ opt.desc }}</span>
            </label>
          </div>
          <span v-if="fieldError('businessTypes') || fieldError('types')" class="rform__error">
            {{ fieldError('businessTypes') || fieldError('types') }}
          </span>
        </div>

        <div v-if="loadingConfig" class="supplier-signup__loading">
          <span class="loading-spinner"></span>
          Loading form…
        </div>

        <ReusableForm v-else id="supplier_signup_form" :loading="submitting" submit-label="Submit Application"
          :show-cancel="false" @submit.prevent="handleSubmit">
          <div v-if="topError" class="rform__error supplier-signup__top-error">
            <i class="fa fa-triangle-exclamation"></i>
            {{ topError }}
          </div>

          <!-- ============ Company Information ============ -->
          <div class="section-block section-block--company">
            <div class="section-head">
              <span class="section-head__icon">🏢</span>
              <div>
                <h4 class="section-head__title">Company Information</h4>
                <p class="section-head__desc">The basics — who you are and how to reach you.</p>
              </div>
            </div>

            <div class="rform__row">
              <div class="rform__field">
                <label class="rform__label rform__label--required">Company Name</label>
                <input v-model="form.company_name" type="text" class="rform__input"
                  :class="{ 'is-invalid': fieldError('company_name') }" placeholder="Enter company name"
                  :readonly="lockedFromLookup" ref="company_name" id="company_name" name="company_name" />
                <span v-if="fieldError('company_name')" class="rform__error">{{ fieldError('company_name') }}</span>
              </div>

              <div class="rform__field">
                <label class="rform__label rform__label--required">Email</label>
                <input v-model="form.email" type="email" class="rform__input"
                  :class="{ 'is-invalid': fieldError('email') }" placeholder="Enter email" @blur="checkEmail"
                  ref="email" id="email" name="email" />
                <!-- <span
                  v-if="emailMessage"
                  class="rform__help"
                  :style="{ color: emailMessageIsError ? '#b3261e' : '#1a7f4b' }"
                >
                  {{ emailMessage }}
                </span> -->
                <span v-if="fieldError('email')" class="rform__error">{{ fieldError('email') }}</span>
              </div>

              <div class="rform__field">
                <label class="rform__label rform__label--required">Phone</label>
                <input v-model="form.phone" type="tel" ref="phone" name="phone" id="phone" class="rform__input"
                  :class="{ 'is-invalid': fieldError('phone') }" placeholder="Enter phone"
                  :readonly="lockedFromLookup" />
                <span v-if="fieldError('phone')" class="rform__error">{{ fieldError('phone') }}</span>
              </div>
            </div>

            <div class="rform__row">
              <div class="rform__field">
                <label class="rform__label rform__label--required">Address</label>
                <input v-model="form.physical_address" type="text" class="rform__input" placeholder="Enter address"
                  :class="{ 'is-invalid': fieldError('physical_address') }" :readonly="lockedFromLookup"
                  ref="physical_address" id="physical_address" name="physical_address" />
                <span v-if="fieldError('physical_address')" class="rform__error">{{ fieldError('physical_address')
                  }}</span>
              </div>
              <div class="rform__field">
                <label class="rform__label rform__label--required">City</label>
                <input v-model="form.street_city" type="text" class="rform__input" placeholder="Enter city"
                  :class="{ 'is-invalid': fieldError('street_city') }" :readonly="lockedFromLookup" ref="street_city"
                  id="street_city" name="street_city" />
                <span v-if="fieldError('street_city')" class="rform__error">{{ fieldError('street_city') }}</span>
              </div>
              <div class="rform__field">
                <label class="rform__label rform__label--required">State / Province</label>
                <input v-model="form.state" type="text" class="rform__input" placeholder="Enter state"
                  :class="{ 'is-invalid': fieldError('state') }" :readonly="lockedFromLookup" ref="state" id="state"
                  name="state" />
                <span v-if="fieldError('state')" class="rform__error">{{ fieldError('state') }}</span>
              </div>
            </div>

            <div class="rform__row">
              <div class="rform__field">
                <label class="rform__label">Postal Code</label>
                <input v-model="form.postal_code" type="text" class="rform__input" placeholder="Postal / ZIP code"
                  :class="{ 'is-invalid': fieldError('postal_code') }" :readonly="lockedFromLookup" ref="postal_code"
                  id="postal_code" name="postal_code" />
                <span v-if="fieldError('postal_code')" class="rform__error">{{ fieldError('postal_code') }}</span>
              </div>
              <div class="rform__field">
                <label class="rform__label">Logo</label>
                <input type="file" class="rform__input" accept="image/*" @change="onSimpleFile($event, 'logo')"
                  ref="logo" id="logo" name="logo" />
                <span class="rform__help">200 × 60 px</span>
                <span v-if="fieldError('logo')" class="rform__error">{{ fieldError('logo') }}</span>
              </div>
            </div>
          </div>

          <!-- ============ Manufacturer-only fields ============ -->
          <div v-if="businessTypes.includes('manufacturer')" class="section-block section-block--manufacturing">
            <div class="section-head">
              <span class="section-head__icon">🏭</span>
              <div>
                <h4 class="section-head__title">Manufacturing Details</h4>
                <p class="section-head__desc">How your products are manufactured.</p>
                <span class="section-head__tag section-head__tag--manufacturing">🏭 Manufacturers only</span>
              </div>
            </div>
            <div class="rform__row">
              <div class="rform__field">
                <label class="rform__label rform__label--required">Manufacturing Site</label>
                <div class="rform__select-wrap">
                  <select v-model="form.manufacturing_type" class="rform__select"
                    :class="{ 'is-invalid': fieldError('manufacturing_type') }" ref="manufacturing_type"
                    id="manufacturing_type" name="manufacturing_type">
                    <option value="">Select manufacturing site</option>
                    <option value="internal">Internal</option>
                    <option value="external">External</option>
                  </select>
                </div>
                <span v-if="fieldError('manufacturing_type')" class="rform__error">{{ fieldError('manufacturing_type')
                  }}</span>
              </div>

              <div v-if="form.manufacturing_type === 'external'" class="rform__field">
                <label class="rform__label rform__label--required">Manufacturer Category</label>
                <div class="rform__select-wrap">
                  <select v-model="form.product_brand_type" class="rform__select"
                    :class="{ 'is-invalid': fieldError('product_brand_type') }" ref="product_brand_type"
                    id="product_brand_type" name="product_brand_type">
                    <option value="">Select category</option>
                    <option value="private_label">Private Label</option>
                    <option value="co_mfg">Co Manufacturer</option>
                    <option value="external_brand">External Brand</option>
                  </select>
                </div>
                <span v-if="fieldError('product_brand_type')" class="rform__error">{{ fieldError('product_brand_type')
                  }}</span>
              </div>

              <div v-if="form.manufacturing_type === 'external'" class="rform__field">
                <label class="rform__label rform__label--required">FP-Manufacturer Type</label>
                <div class="rform__select-wrap">
                  <select v-model="form.external_type" class="rform__select"
                    :class="{ 'is-invalid': fieldError('external_type') }" ref="external_type" id="external_type"
                    name="external_type">
                    <option value="">Select FP-manufacturer type</option>
                    <option value="inland">Inland FP-Manufacturer</option>
                    <option value="overseas">Overseas</option>
                    <option value="cross-border">Cross Border FP-Manufacturer</option>
                  </select>
                </div>
                <span v-if="fieldError('external_type')" class="rform__error">{{ fieldError('external_type') }}</span>
              </div>
            </div>
          </div>

          <!-- ============ Supplier-only production site fields ============ -->
          <div v-if="businessTypes.includes('supplier')" class="section-block section-block--production">
            <div class="section-head">
              <span class="section-head__icon">🚚</span>
              <div>
                <h4 class="section-head__title">Production Site Info</h4>
                <p class="section-head__desc">Where the goods you supply are actually produced.</p>
                <span class="section-head__tag section-head__tag--production">🌾 Suppliers only</span>
              </div>
            </div>
            <div class="rform__row">
              <div class="rform__field">
                <label class="rform__label">Production  City</label>
                <input v-model="form.production_city" type="text" class="rform__input"
                  placeholder="Enter manufacturing city" ref="production_city" id="production_city"
                  name="production_city" />
                <span v-if="fieldError('production_city')" class="rform__error">{{ fieldError('production_city')
                  }}</span>
              </div>
              <div class="rform__field">
                <label class="rform__label">Production Province/State</label>
                <input v-model="form.production_state" type="text" class="rform__input"
                  placeholder="Enter production state" ref="production_state" id="production_state"
                  name="production_state" />
                <span v-if="fieldError('production_state')" class="rform__error">{{ fieldError('production_state')
                  }}</span>
              </div>
              <div class="rform__field">
                <label class="rform__label">Production Address</label>
                <input v-model="form.production_address" type="text" class="rform__input"
                  placeholder="Enter production address" ref="production_address" id="production_address"
                  name="production_address" />
                <span v-if="fieldError('production_address')" class="rform__error">{{ fieldError('production_address')
                  }}</span>
              </div>
            </div>
            <div class="rform__row">
              <div class="rform__field">
                <label class="rform__label">Production Postal Code</label>
                <input v-model="form.production_postcode" type="text" class="rform__input" placeholder="Enter postcode"
                  ref="production_postcode" id="production_postcode" name="production_postcode" />
                <span v-if="fieldError('production_postcode')" class="rform__error">{{ fieldError('production_postcode')
                  }}</span>
              </div>
            </div>
          </div>

          <!-- ============ POC ============ -->
          <div class="section-block section-block--poc">
            <div class="section-head">
              <span class="section-head__icon">👤</span>
              <div>
                <h4 class="section-head__title">Primary Point of Contact</h4>
                <p class="section-head__desc">Who should we reach out to first?</p>
              </div>
            </div>
            <div class="rform__row">
              <div class="rform__field">
                <label class="rform__label">Emergency Contact Number</label>
                <input v-model="form.emergency_contact" type="text" class="rform__input"
                  placeholder="Enter emergency contact number" :readonly="lockedFromLookup" ref="emergency_contact"
                  id="emergency_contact" name="emergency_contact" />
                <span v-if="fieldError('emergency_contact')" class="rform__error">{{ fieldError('emergency_contact')
                  }}</span>
              </div>
              <div class="rform__field">
                <label class="rform__label rform__label--required">POC Name</label>
                <input v-model="form.poc" type="text" class="rform__input" placeholder="POC name"
                  :class="{ 'is-invalid': fieldError('poc') }" :readonly="lockedFromLookup" ref="poc" id="poc"
                  name="poc" />
                <span v-if="fieldError('poc')" class="rform__error">{{ fieldError('poc') }}</span>
              </div>
              <div class="rform__field">
                <label class="rform__label">Title</label>
                <input v-model="form.title" type="text" class="rform__input" placeholder="Enter title"
                  :readonly="lockedFromLookup" ref="title" id="title" name="title" />
                <span v-if="fieldError('title')" class="rform__error">{{ fieldError('title') }}</span>
              </div>
            </div>
          </div>

          <!-- ============ QA Contact ============ -->
          <div class="section-block section-block--qa">
            <div class="section-head">
              <span class="section-head__icon">🔬</span>
              <div>
                <h4 class="section-head__title">QA Contact</h4>
                <p class="section-head__desc">Who handles quality and compliance questions.</p>
              </div>
            </div>
            <div class="rform__row">
              <div class="rform__field">
                <label class="rform__label">QA Name</label>
                <input v-model="form.qa_name" type="text" class="rform__input" placeholder="Enter QA name"
                  :readonly="lockedFromLookup" ref="qa_name" id="qa_name" name="qa_name" />
                <span v-if="fieldError('qa_name')" class="rform__error">{{ fieldError('qa_name') }}</span>
              </div>
              <div class="rform__field">
                <label class="rform__label">QA Title</label>
                <input v-model="form.qa_title" type="text" class="rform__input" placeholder="Enter title"
                  :readonly="lockedFromLookup" ref="qa_title" id="qa_title" name="qa_title" />
                <span v-if="fieldError('qa_title')" class="rform__error">{{ fieldError('qa_title') }}</span>
              </div>
              <div class="rform__field">
                <label class="rform__label">QA Email</label>
                <input v-model="form.qa_email" type="email" class="rform__input" placeholder="Enter QA email"
                  :class="{ 'is-invalid': fieldError('qa_email') }" :readonly="lockedFromLookup" ref="qa_email"
                  id="qa_email" name="qa_email" />
                <span v-if="fieldError('qa_email')" class="rform__error">{{ fieldError('qa_email') }}</span>
              </div>
            </div>
            <div class="rform__row">
              <div class="rform__field">
                <label class="rform__label">QA Phone</label>
                <input v-model="form.qa_phone" type="tel" class="rform__input" placeholder="Enter QA phone"
                  :readonly="lockedFromLookup" ref="qa_phone" id="qa_phone" name="qa_phone" />
                <span v-if="fieldError('qa_phone')" class="rform__error">{{ fieldError('qa_phone') }}</span>
              </div>
            </div>
          </div>

          <!-- ============ Compliance Questionnaire ============ -->
          <div class="section-block section-block--compliance"
            :class="{ 'section-block--has-error': sectionHasError(complianceErrorKeys) }">
            <div class="section-head">
              <span class="section-head__icon">📋</span>
              <div>
                <h4 class="section-head__title">Regulatory Compliance Questionnaire</h4>
                <p class="section-head__desc">Indicate YES / NO / N/A and provide remarks where applicable.</p>
              </div>
            </div>

            <div v-for="q in questions" :key="q.key" class="q-row"
              :class="{ 'q-row--error': fieldError(`compliance.${q.key}.answer`) || fieldError(`compliance.${q.key}.remarks`) }">
              <div class="q-num">{{ q.num }}</div>
              <div class="q-content">
                <div class="q-header">
                  <div class="q-text" v-html="q.text"></div>
                  <div class="yn-group">
                    <label v-for="opt in q.opts" :key="opt" class="yn-label" :class="ynClass(opt)">
                      <input type="radio" :name="`compliance[${q.key}][answer]`" :value="opt"
                        v-model="compliance[q.key].answer" :id="`compliance_${q.key}_${opt}`"
                        :ref="`compliance_${q.key}_${opt}`" />
                      {{ ynIcon(opt) }} {{ opt }}
                    </label>
                  </div>
                </div>
                <span v-if="fieldError(`compliance.${q.key}.answer`)" class="rform__error">
                  {{ fieldError(`compliance.${q.key}.answer`) }}
                </span>

                <ul v-if="q.sub && q.sub.length" class="q-sub">
                  <li v-for="(s, i) in q.sub" :key="i">{{ s }}</li>
                </ul>

                <div v-if="q.has_remarks === 'yes' && compliance[q.key].answer === 'NO'"
                  class="rform__field supplier-signup__remarks">
                  <textarea v-model="compliance[q.key].remarks" class="rform__textarea" rows="1"
                    placeholder="Remarks..." :class="{ 'is-invalid': fieldError(`compliance.${q.key}.remarks`) }"
                    :ref="`compliance_${q.key}_remarks`" :id="`compliance_${q.key}_remarks`"
                    :name="`compliance[${q.key}][remarks]`"></textarea>
                  <span v-if="fieldError(`compliance.${q.key}.remarks`)" class="rform__error">
                    {{ fieldError(`compliance.${q.key}.remarks`) }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- ============ IP Certifications ============ -->
          <div class="section-block section-block--ipcert">
            <div class="section-head">
              <span class="section-head__icon">🌱</span>
              <div>
                <h4 class="section-head__title">Identity Preserved Certifications</h4>
                <p class="section-head__desc">Certifications, standards, or claims you maintain.</p>
              </div>
            </div>
            <div class="rform__field">
              <div class="rform__radio-group">
                <label class="rform__radio">
                  <input type="radio" value="yes" v-model="ipCertOption" name="ip_cert_option" id="ip_cert_option_yes"
                    ref="ip_cert_option_yes" />
                  <span class="rform__radio-dot"></span>
                  <span class="rform__radio-text">Yes</span>
                </label>
                <label class="rform__radio">
                  <input type="radio" value="na" v-model="ipCertOption" name="ip_cert_option" id="ip_cert_option_na"
                    ref="ip_cert_option_na" />
                  <span class="rform__radio-dot"></span>
                  <span class="rform__radio-text">N/A</span>
                </label>
              </div>
              <span v-if="fieldError('ip_cert_option')" class="rform__error">{{ fieldError('ip_cert_option') }}</span>
            </div>

            <template v-if="ipCertOption === 'yes'">
              <p class="rform__help rform__intro mt-2">Check all that apply and attach the certificate file.</p>

              <div class="ip-grid">
                <div v-for="c in ipCerts" :key="c.key" class="ip-item">
                  <label class="rform__checkbox">
                    <input type="checkbox" v-model="ipCertsForm[c.key].selected" :name="`ip_certs[${c.key}][selected]`"
                      :id="`ip_cert_${c.key}`" :ref="`ip_cert_${c.key}`" />
                    <span class="rform__checkbox-box"></span>
                    <span class="rform__checkbox-text">{{ c.icon }} {{ c.label }}</span>
                  </label>
                  <div v-if="ipCertsForm[c.key].selected" class="ip-file-zone">
                    <label class="rform__label">Attach Certificate File</label>
                    <input type="file" class="rform__input" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                      @change="e => (ipCertsForm[c.key].file = e.target.files[0])" :name="`ip_certs[${c.key}][file]`"
                      :id="`ip_cert_${c.key}_file`" :ref="`ip_cert_${c.key}_file`" />
                    <span v-if="fieldError(`ip_certs.${c.key}.file`)" class="rform__error">
                      {{ fieldError(`ip_certs.${c.key}.file`) }}
                    </span>
                  </div>
                </div>
              </div>

              <div v-for="(extra, idx) in ipExtraCerts" :key="extra.id" class="rform__row supplier-signup__extra-row">
                <div class="rform__field mt-3">
                  <label class="rform__label">Certification Name</label>
                  <input v-model="extra.title" type="text" class="rform__input" placeholder="e.g. Fairtrade"
                    :name="`ip_certs[${extra.id}][title]`" :id="`ip_cert_${extra.id}_title`"
                    :ref="`ip_cert_${extra.id}_title`" />
                </div>
                <div class="rform__field mt-3">
                  <label class="rform__label">Attach File</label>
                  <input type="file" class="rform__input" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                    @change="e => (extra.file = e.target.files[0])" :name="`ip_certs[${extra.id}][file]`"
                    :id="`ip_cert_${extra.id}_file`" :ref="`ip_cert_${extra.id}_file`" />
                </div>
                <button type="button"
                  class="rform__btn mt-3 rform__btn--ghost rform__btn--ghost-danger rform__btn--sm supplier-signup__remove-btn"
                  @click="removeIpExtra(idx)">
                  Remove
                </button>
              </div>
              <div class="rform__btn-wrap">
                <button type="button" class="rform__btn--add rform__btn--add-sm" @click="addIpExtra">
                  Add More
                </button>
              </div>
            </template>
          </div>

          <!-- ============ Food Safety Certification ============ -->
          <div class="section-block section-block--foodsafety"
            :class="{ 'section-block--has-error': fieldError('foodSafetyCert') || fieldError('certificates') }">
            <div class="section-head">
              <span class="section-head__icon">🛡️</span>
              <div>
                <h4 class="section-head__title">Food Safety Management Certification</h4>
                <p class="section-head__desc">Do you maintain an accredited food safety certification?</p>
              </div>
            </div>
            <div class="rform__field">
              <div class="rform__radio-group">
                <label class="rform__radio">
                  <input type="radio" value="yes" v-model="foodSafetyCert" name="foodSafetyCert"
                    id="food_safety_cert_yes" ref="food_safety_cert_yes" />
                  <span class="rform__radio-dot"></span>
                  <span class="rform__radio-text">Yes</span>
                </label>
                <label class="rform__radio">
                  <input type="radio" value="no" v-model="foodSafetyCert" name="foodSafetyCert" id="food_safety_cert_no"
                    ref="food_safety_cert_no" />
                  <span class="rform__radio-dot"></span>
                  <span class="rform__radio-text">No</span>
                </label>
              </div>
              <span v-if="fieldError('foodSafetyCert')" class="rform__error">{{ fieldError('foodSafetyCert') }}</span>
            </div>

            <template v-if="foodSafetyCert === 'yes'">
              <div v-for="(cert, idx) in selectedCertificates" :key="cert.id" class="cert-card"
                :class="{ 'cert-card--error': fieldError(`certificates.${certificateKey(cert)}`) }">
                <div class="cert-card__header">
                  <strong>{{ cert.isOther ? (cert.title || 'Other Certificate') : cert.value }}</strong>
                  <button type="button" class="rform__remove-btn rform__remove-btn--sm" @click="removeCertificate(idx)">
                    Remove
                  </button>
                </div>
                <span v-if="fieldError(`certificates.${certificateKey(cert)}`)" class="rform__error">
                  {{ fieldError(`certificates.${certificateKey(cert)}`) }}
                </span>
                <div class="rform__row">
                  <div v-if="cert.isOther" class="rform__field">
                    <label class="rform__label rform__label--required">Certificate Title</label>
                    <input v-model="cert.title" type="text" class="rform__input"
                      :class="{ 'is-invalid': fieldError(`certificates.${certificateKey(cert)}.title`) }"
                      placeholder="e.g. My Custom Certificate" :name="`certificates[${certificateKey(cert)}][title]`"
                      :id="`certificate_${certificateKey(cert)}_title`"
                      :ref="`certificate_${certificateKey(cert)}_title`" />
                    <span v-if="fieldError(`certificates.${certificateKey(cert)}.title`)" class="rform__error">
                      {{ fieldError(`certificates.${certificateKey(cert)}.title`) }}
                    </span>
                  </div>
                  <div class="rform__field">
                    <label class="rform__label rform__label--required">Certificate File</label>
                    <input type="file" class="rform__input"
                      :class="{ 'is-invalid': fieldError(`certificates.${certificateKey(cert)}.file`) }"
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" @change="e => (cert.file = e.target.files[0])"
                      :name="`certificates[${certificateKey(cert)}][file]`"
                      :id="`certificate_${certificateKey(cert)}_file`"
                      :ref="`certificate_${certificateKey(cert)}_file`" />
                    <span v-if="fieldError(`certificates.${certificateKey(cert)}.file`)" class="rform__error">
                      {{ fieldError(`certificates.${certificateKey(cert)}.file`) }}
                    </span>
                  </div>
                  <div class="rform__field">
                    <label class="rform__label rform__label--required">Expiry Date</label>
                    <input v-model="cert.expiryDate" type="date" class="rform__input"
                      :class="{ 'is-invalid': fieldError(`certificates.${certificateKey(cert)}.expiryDate`) }"
                      :name="`certificates[${certificateKey(cert)}][expiry_date]`"
                      :id="`certificate_${certificateKey(cert)}_expiry_date`"
                      :ref="`certificate_${certificateKey(cert)}_expiry_date`" />
                    <span v-if="fieldError(`certificates.${certificateKey(cert)}.expiryDate`)" class="rform__error">
                      {{ fieldError(`certificates.${certificateKey(cert)}.expiryDate`) }}
                    </span>
                  </div>
                  <div class="rform__field">
                    <label class="rform__label">Comment</label>
                    <input v-model="cert.comment" type="text" class="rform__input" placeholder="Optional notes"
                      :name="`certificates[${certificateKey(cert)}][comment]`"
                      :id="`certificate_${certificateKey(cert)}_comment`"
                      :ref="`certificate_${certificateKey(cert)}_comment`" />
                  </div>
                </div>
              </div>

              <div class="rform__row--2 mt-3">
                <div class="rform__field">
                  <label class="rform__label">Add a certificate</label>
                  <div class="rform__select-wrap">
                    <select v-model="certToAdd" class="rform__select mt-2" @change="addCertificate" ref="cert_to_add"
                      id="cert_to_add" name="cert_to_add">
                      <option value="">— Select a certificate to add —</option>
                      <optgroup label="GFSI Certifications">
                        <option value="SQF">SQF</option>
                        <option value="BRCGS">BRCGS</option>
                        <option value="FSSC 22000">FSSC 22000</option>
                        <option value="IFS">IFS</option>
                      </optgroup>
                      <optgroup label="Other Food Safety Certifications">
                        <option value="Global G.A.P.">Global G.A.P.</option>
                        <option value="Canada G.A.P.">Canada G.A.P.</option>
                        <option value="ISO 22000">ISO 22000</option>
                        <option value="HACCP">HACCP</option>
                        <option value="GMP">GMP</option>
                        <option value="CFIA Licence">CFIA Licence</option>
                        <option value="FSMA Compliance Audit">FSMA Compliance Audit</option>
                        <option value="PrimusGFS">PrimusGFS</option>
                      </optgroup>
                      <optgroup label="Custom">
                        <option value="__other__">Other (specify title)</option>
                      </optgroup>
                    </select>
                  </div>
                </div>
              </div>
              <span v-if="fieldError('certificates')" class="rform__error">{{ fieldError('certificates') }}</span>

              <p v-if="!selectedCertificates.length" class="rform__help supplier-signup__intro mt-2">
                No certificates added yet. Select one above.
              </p>
            </template>

            <!-- Non-certified questionnaire -->
            <template v-else-if="foodSafetyCert === 'no'">
              <div class="section-subhead">
                <span class="section-subhead__icon">📝</span>
                <div>
                  <h5 class="section-subhead__title">Non-Certified Questionnaire</h5>
                  <p class="section-subhead__desc">Please indicate YES/NO and provide detail where requested.</p>
                </div>
              </div>
              <span v-if="fieldError('non_cert')" class="rform__error">{{ fieldError('non_cert') }}</span>

              <div v-for="q in nonCertQuestions" :key="q.key" class="q-row"
                :class="{ 'q-row--error': fieldError(`non_cert.${q.key}.answer`) || fieldError(`non_cert.${q.key}.remarks`) }">
                <div class="q-num">{{ q.num }}</div>
                <div class="q-content">
                  <div class="q-header">
                    <div class="q-text">{{ q.text }}</div>
                    <div class="yn-group">
                      <label class="yn-label yn-yes">
                        <input type="radio" value="YES" v-model="nonCert[q.key].answer"
                          :name="`non_cert[${q.key}][answer]`" :id="`non_cert_${q.key}_yes`"
                          :ref="`non_cert_${q.key}_yes`" />✓ YES
                      </label>
                      <label class="yn-label yn-no">
                        <input type="radio" value="NO" v-model="nonCert[q.key].answer"
                          :name="`non_cert[${q.key}][answer]`" :id="`non_cert_${q.key}_no`"
                          :ref="`non_cert_${q.key}_no`" />✕ NO
                      </label>
                    </div>
                  </div>
                  <span v-if="fieldError(`non_cert.${q.key}.answer`)" class="rform__error">
                    {{ fieldError(`non_cert.${q.key}.answer`) }}
                  </span>

                  <div v-if="q.has_details && nonCert[q.key].answer === 'YES'"
                    class="rform__row--2 supplier-signup__details-row">
                    <div class="rform__field">
                      <label class="rform__label">{{ q.details_label }}</label>
                      <textarea v-if="q.details_type === 'textarea'" v-model="nonCert[q.key].details"
                        class="rform__textarea" :class="{ 'is-invalid': fieldError(`non_cert.${q.key}.details`) }"
                        :placeholder="q.details_placeholder" :name="`non_cert[${q.key}][details]`"
                        :id="`non_cert_${q.key}_details`" :ref="`non_cert_${q.key}_details`"></textarea>
                      <input v-else v-model="nonCert[q.key].details" :type="q.details_type" class="rform__input"
                        :class="{ 'is-invalid': fieldError(`non_cert.${q.key}.details`) }"
                        :placeholder="q.details_placeholder" :name="`non_cert[${q.key}][details]`"
                        :id="`non_cert_${q.key}_details`" :ref="`non_cert_${q.key}_details`" />
                      <span v-if="fieldError(`non_cert.${q.key}.details`)" class="rform__error">
                        {{ fieldError(`non_cert.${q.key}.details`) }}
                      </span>
                    </div>
                    <div v-if="q.has_second_detail" class="rform__field">
                      <label class="rform__label">{{ q.second_label }}</label>
                      <input v-model="nonCert[q.key].second_detail" :type="q.second_type" class="rform__input"
                        :class="{ 'is-invalid': fieldError(`non_cert.${q.key}.second_detail`) }"
                        :placeholder="q.second_placeholder" :name="`non_cert[${q.key}][second_detail]`"
                        :id="`non_cert_${q.key}_second_detail`" :ref="`non_cert_${q.key}_second_detail`" />
                      <span v-if="fieldError(`non_cert.${q.key}.second_detail`)" class="rform__error">
                        {{ fieldError(`non_cert.${q.key}.second_detail`) }}
                      </span>
                    </div>
                  </div>

                  <div v-if="q.has_remarks && nonCert[q.key].answer === 'NO'"
                    class="rform__field supplier-signup__remarks">
                    <label class="rform__label" :class="{ 'rform__label--required': q.remarks_required }">
                      {{ q.remarks_label || 'Remarks' }}
                    </label>
                    <textarea v-if="q.remarks_type === 'textarea'" v-model="nonCert[q.key].remarks"
                      class="rform__textarea" :rows="q.remarks_rows || 3"
                      :placeholder="q.remarks_placeholder || 'Please provide remarks...'"
                      :class="{ 'is-invalid': fieldError(`non_cert.${q.key}.remarks`) }"
                      :name="`non_cert[${q.key}][remarks]`" :id="`non_cert_${q.key}_remarks`"
                      :ref="`non_cert_${q.key}_remarks`"></textarea>
                    <input v-else v-model="nonCert[q.key].remarks" type="text" class="rform__input"
                      :placeholder="q.remarks_placeholder || 'Please provide remarks...'"
                      :class="{ 'is-invalid': fieldError(`non_cert.${q.key}.remarks`) }"
                      :name="`non_cert[${q.key}][remarks]`" :id="`non_cert_${q.key}_remarks`"
                      :ref="`non_cert_${q.key}_remarks`" />
                    <span v-if="fieldError(`non_cert.${q.key}.remarks`)" class="rform__error">
                      {{ fieldError(`non_cert.${q.key}.remarks`) }}
                    </span>
                  </div>
                </div>
              </div>
            </template>
          </div>

          <!-- ============ Services (service_provider only) ============ -->
          <div v-if="businessTypes.includes('service_provider')" class="section-block section-block--services">
            <div class="section-head">
              <span class="section-head__icon">🤝</span>
              <div>
                <h4 class="section-head__title">Services Offered</h4>
                <p class="section-head__desc">Select each category of service you provide.</p>
                <span class="section-head__tag section-head__tag--services">🤝 Service Providers only</span>
              </div>
            </div>

            <div class="rform__field">
              <label class="rform__label">Services</label>
              <MultiSelect v-model="servicesSelected" :options="serviceOptions" placeholder="Select services…"
                track-by="id" id="services_selected" ref="services_selected" />
              <span v-if="fieldError('services')" class="rform__error">{{ fieldError('services') }}</span>
            </div>

            <div v-if="hasOtherServiceSelected" class="rform__field">
              <label class="rform__label rform__label--required">Specify other service</label>
              <input v-model="servicesOtherLabel" type="text" class="rform__input"
                :class="{ 'is-invalid': fieldError('services.other_label') }" placeholder="e.g. Cold-chain logistics"
                ref="services_other_label" id="services_other_label" name="services_other_label" />
              <span v-if="fieldError('services.other_label')" class="rform__error">
                {{ fieldError('services.other_label') }}
              </span>
            </div>
          </div>

          <!-- ============ Materials (supplier only) — draft + add-to-list flow ============ -->
          <div v-if="businessTypes.includes('supplier')" class="section-block section-block--materials"
            :class="{ 'section-block--has-error': fieldError('materials') }">
            <div class="section-head">
              <span class="section-head__icon">📦</span>
              <div>
                <h4 class="section-head__title">Materials</h4>
                <p class="section-head__desc">Add each material you supply or produce.</p>
                <span class="section-head__tag section-head__tag--materials">🌾 Suppliers only</span>
              </div>
            </div>

            <!-- Draft entry form -->
            <div class="draft-card" :class="{ 'draft-card--editing': editingMaterialIndex !== null }">
              <div class="draft-card__label" v-if="editingMaterialIndex !== null">
                <i class="fa fa-pencil"></i> Editing material — update the fields and click "Update Material"
              </div>

              <div class="rform__row mt-3">
                <div class="rform__field">
                  <label class="rform__label rform__label--required">Name</label>
                  <input v-model="materialDraft.name" type="text" class="rform__input"
                    placeholder="e.g. Organic Cane Sugar" />
                </div>
                <div class="rform__field">
                  <label class="rform__label rform__label--required">Type</label>
                  <div class="rform__select-wrap">
                    <select v-model="materialDraft.type" class="rform__select">
                      <option value="">Select type</option>
                      <option value="raw_material">Raw Material</option>
                      <option value="packaging">Packaging Material</option>
                      <option value="finished_material">Finished Material</option>
                    </select>
                  </div>
                </div>
                <div class="rform__field">
                  <label class="rform__label">Lead Time</label>
                  <input v-model="materialDraft.lead_time" type="text" class="rform__input"
                    placeholder="e.g. 4-6 weeks" />
                </div>
              </div>

              <div class="rform__row mt-3">
                <div class="rform__field">
                  <label class="rform__label">Packaging Size</label>
                  <input v-model="materialDraft.packaging_size" type="text" class="rform__input"
                    placeholder="e.g. 25 kg bag" />
                </div>
                <div class="rform__field">
                  <label class="rform__label">Packaging Type</label>
                  <div class="rform__select-wrap">
                    <select v-model="materialDraft.packaging_type" class="rform__select">
                      <option value="">Select packaging type</option>
                      <option value="glass">Glass</option>
                      <option value="plastic">Plastic</option>
                      <option value="paper_box">Paper Box</option>
                      <option value="metal">Metal</option>
                    </select>
                  </div>
                </div>
                <div class="rform__field">
                  <label class="rform__label">Upload Documents</label>
                  <input type="file" class="rform__input" multiple accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                    @change="e => (materialDraft.files = toRawFiles(e.target.files))" />
                  <span v-if="materialDraft.files.length" class="rform__help">
                    {{ materialDraft.files.length }} file(s) selected
                  </span>
                </div>
              </div>

              <div class="row py-3 px-1 mt-2">
                <div class="col-md-6">
                  <div class="rform__field">
                    <label class="rform__label">Allergens List</label>
                    <MultiSelect v-model="materialDraft.allergens" :options="allergensOptions"
                      placeholder="Select allergens…" track-by="id" />
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="rform__field">
                    <label class="rform__label">Sub Ingredients</label>
                    <textarea v-model="materialDraft.sub_ingredients" class="rform__textarea" rows="2"
                      placeholder="List sub-ingredients, comma separated"></textarea>
                  </div>
                </div>
              </div>

              <span v-if="materialDraftError" class="rform__error">{{ materialDraftError }}</span>

              <div class="draft-card__actions">
                <button v-if="editingMaterialIndex !== null" type="button" class="rform__btn rform__btn--ghost"
                  @click="cancelMaterialEdit">
                  Cancel Edit
                </button>
                <button type="button" class="rform__btn--add" @click="addOrUpdateMaterial">
                  <i :class="editingMaterialIndex !== null ? 'fa fa-check' : 'fa fa-plus'"></i>
                  {{ editingMaterialIndex !== null ? 'Update Material' : 'Add to List' }}
                </button>
              </div>
            </div>

            <!-- Added materials table -->
            <div v-if="materials.length" class="added-table-wrap">
              <table class="added-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Packaging</th>
                    <th>Lead Time</th>
                    <th class="added-table__actions-col">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(material, idx) in materials" :key="material.id"
                    :class="{ 'is-editing': editingMaterialIndex === idx }">
                    <td class="added-table__strong">{{ material.name }}</td>
                    <td>{{ formatSlug(material.type) }}</td>
                    <td>
                      {{ material.packaging_size || '—' }}
                      <span v-if="material.packaging_type"> · {{ formatSlug(material.packaging_type) }}</span>
                    </td>
                    <td>{{ material.lead_time || '—' }}</td>
                    <td class="added-table__actions">
                      <button type="button" class="table-icon-btn" @click="editMaterial(idx)"
                        aria-label="Edit material">
                        <i class="fa fa-pencil"></i>
                      </button>
                      <button type="button" class="table-icon-btn table-icon-btn--danger" @click="removeMaterial(idx)"
                        aria-label="Remove material">
                        <i class="fa fa-trash"></i>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p v-else class="rform__help rform__intro">No materials added yet — fill the form above and click "Add to
              List".</p>
            <span v-if="fieldError('materials')" class="rform__error">{{ fieldError('materials') }}</span>
          </div>

          <div v-if="businessTypes.includes('manufacturer')" class="section-block section-block--products"
            :class="{ 'section-block--has-error': fieldError('products') }">
            <div class="section-head">
              <span class="section-head__icon">🧴</span>
              <div>
                <h4 class="section-head__title">Products</h4>
                <p class="section-head__desc">Add each product you manufacture.</p>
                <span class="section-head__tag section-head__tag--products">🏭 Manufacturers only</span>
              </div>
            </div>

            <div class="draft-card" :class="{ 'draft-card--editing': editingProductIndex !== null }">
              <div class="draft-card__label" v-if="editingProductIndex !== null">
                <i class="fa fa-pencil"></i> Editing product — update the fields and click "Update Product"
              </div>

              <div class="rform__row mt-3">
                <div class="rform__field">
                  <label class="rform__label rform__label--required">Name</label>
                  <input v-model="productDraft.name" type="text" class="rform__input"
                    placeholder="e.g. Organic Trail Mix" />
                </div>
                <div class="rform__field">
                  <label class="rform__label">Product Application</label>
                  <div class="rform__select-wrap">
                    <select v-model="productDraft.application" class="rform__select">
                      <option value="">Select application type</option>
                      <option value="retail">Retail</option>
                      <option value="wholesale">Wholesale</option>
                      <option value="food_service">Food Service</option>
                      <option value="institutional_sale">Institutional Sale</option>
                    </select>
                  </div>
                </div>
                <div class="rform__field">
                  <label class="rform__label">Product Code / SKU</label>
                  <input v-model="productDraft.sku" type="text" class="rform__input" placeholder="e.g. TM-1024" />
                </div>
              </div>

              <div class="rform__row mt-3">
                <div class="rform__field">
                  <label class="rform__label rform__label--required">Production Type</label>
                  <div class="rform__select-wrap">
                    <select v-model="productDraft.production_type" class="rform__select">
                      <option value="">--Select--</option>
                      <option value="wip">WIP (Work In Progress)</option>
                      <option value="fp">FP (Finish Product)</option>
                    </select>
                  </div>
                </div>
                <div class="rform__field">
                  <label class="rform__label rform__label--required">Unit</label>
                  <input v-model="productDraft.unit" type="text" class="rform__input"
                    placeholder="e.g. kg, box, case" />
                </div>
              </div>

              <div class="rform__row mt-3">
                <div class="rform__field" style="grid-column: 1 / -1;">
                  <label class="rform__label">Product Description</label>
                  <textarea v-model="productDraft.description" class="rform__textarea" rows="2"
                    placeholder="Optional — add any details about this product..."></textarea>
                </div>
              </div>

              <span v-if="productDraftError" class="rform__error">{{ productDraftError }}</span>

              <div class="draft-card__actions">
                <button v-if="editingProductIndex !== null" type="button" class="rform__btn rform__btn--ghost"
                  @click="cancelProductEdit">
                  Cancel Edit
                </button>
                <button type="button" class="rform__btn--add" @click="addOrUpdateProduct">
                  <i :class="editingProductIndex !== null ? 'fa fa-check' : 'fa fa-plus'"></i>
                  {{ editingProductIndex !== null ? 'Update Product' : 'Add to List' }}
                </button>
              </div>
            </div>

            <div v-if="products.length" class="added-table-wrap">
              <table class="added-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Application</th>
                    <th>Production Type</th>
                    <th>Unit</th>
                    <th class="added-table__actions-col">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(product, idx) in products" :key="product.id"
                    :class="{ 'is-editing': editingProductIndex === idx }">
                    <td class="added-table__strong">{{ product.name }}</td>
                    <td>{{ formatSlug(product.application) || '—' }}</td>
                    <td>{{ productionTypeLabel(product.production_type) }}</td>
                    <td>{{ product.unit }}</td>
                    <td class="added-table__actions">
                      <button type="button" class="table-icon-btn" @click="editProduct(idx)" aria-label="Edit product">
                        <i class="fa fa-pencil"></i>
                      </button>
                      <button type="button" class="table-icon-btn table-icon-btn--danger" @click="removeProduct(idx)"
                        aria-label="Remove product">
                        <i class="fa fa-trash"></i>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p v-else class="rform__help rform__intro">No products added yet — fill the form above and click "Add to
              List".</p>
            <span v-if="fieldError('products')" class="rform__error">{{ fieldError('products') }}</span>
          </div>

          <!-- ============ Comments ============ -->
          <div class="section-block section-block--comments">
            <div class="section-head">
              <span class="section-head__icon">💬</span>
              <div>
                <h4 class="section-head__title">Anything else?</h4>
                <p class="section-head__desc">Optional — add any additional context.</p>
              </div>
            </div>
            <div class="rform__field">
              <textarea v-model="form.comments" class="rform__textarea" rows="4" placeholder="Write comments here..."
                ref="comments" id="comments" name="comments"></textarea>
              <span v-if="fieldError('comments')" class="rform__error">{{ fieldError('comments') }}</span>
            </div>
          </div>
        </ReusableForm>
      </div>

      <p class="login-card__signup text-center">
        Back To Home?
        <router-link :to="{ name: 'Home' }" class="login-card__signup-link">Home</router-link>
      </p>
    </div>
  </div>
</template>

<script>
import ReusableForm from '@/components/utils/Form.vue'
import { form_api_submit } from '@/utils/FormSubmit'
import MultiSelect from '@/components/MultiSelect.vue'
import { COUNTRIES } from '@/constants/countries'
import { markRaw } from 'vue'
export default {
  name: 'SupplierSignup',
  components: { ReusableForm, MultiSelect },

  emits: ['submitted'],

  data() {
    return {
      checkEmailUrl: 'supplier/check-email',

      businessTypes: [],
      businessTypeOptions: [
        { value: 'supplier', icon: '🌾', label: 'Supplier', desc: 'You supply raw materials or ingredients.' },
        { value: 'manufacturer', icon: '🏭', label: 'Manufacturer', desc: 'You produce or process finished goods.' },
        { value: 'service_provider', icon: '🤝', label: 'Service Provider', desc: 'You offer logistics, QA, or other services.' },
      ],

      loadingConfig: true,
      submitting: false,
      topError: '',

      questions: [],
      ipCerts: [],
      nonCertQuestions: [],

      form: {
        company_name: '',
        email: '',
        phone: '',
        manufacturing_type: '',
        product_brand_type: '',
        external_type: '',
        physical_address: '',
        street_city: '',
        state: '',
        postal_code: '',
        logo: null,
        emergency_contact: '',
        poc: '',
        title: '',
        qa_name: '',
        qa_title: '',
        qa_email: '',
        qa_phone: '',
        production_city: '',
        production_state: '',
        production_address: '',
        production_postcode: '',
        services_description: '',
        comments: '',
        supplier_type: '',
      },
      errors: {},

      lockedFromLookup: false,
      emailMessage: '',
      emailMessageIsError: false,

      compliance: {},

      ipCertOption: 'na',
      ipCertsForm: {},
      ipExtraCerts: [],
      ipExtraCounter: 0,

      foodSafetyCert: '',
      certToAdd: '',
      selectedCertificates: [],
      certCounter: 0,
      addedCertValues: new Set(),

      nonCert: {},

      // ---- Materials: draft + list ----
      materials: [],
      materialCounter: 0,
      materialDraft: this.emptyMaterialDraft(),
      editingMaterialIndex: null,
      materialDraftError: '',

      // ---- Products: draft + list ----
      products: [],
      productCounter: 0,
      productDraft: this.emptyProductDraft(),
      editingProductIndex: null,
      productDraftError: '',

      allergensOptions: [],
      serviceOptions: [
        { value: 'electrical', label: 'Electrical' },
        { value: 'refrigeration', label: 'Refrigeration' },
        { value: 'mechanical', label: 'Mechanical' },
        { value: 'hvac', label: 'HVAC' },
        { value: 'cleaning', label: 'Cleaning' },
        { value: 'welding', label: 'Welding' },
        { value: 'landscaping', label: 'Landscaping' },
        { value: 'fabrication', label: 'Fabrication' },
        { value: 'equipment_installation', label: 'Equipment Installation' },
        { value: 'equipment_maintenance', label: 'Equipment Maintenance' },
        { value: 'pest_control', label: 'Pest Control' },
        { value: 'utility_maintenance', label: 'Utility Maintenance' },
        { value: 'forklift_maintenance', label: 'Forklift Maintenance' },
        { value: 'other', label: 'Other' },
      ],
      servicesSelected: [],
      servicesOtherLabel: '',

      isLoggedIn: !!localStorage.getItem('supplier_id'),
      COUNTRIES,
      countryOptions: COUNTRIES,
    }
  },

  async mounted() {
    await this.loadQues()
  },

  computed: {
    hasOtherServiceSelected() {
      return this.servicesSelected.some((s) => (s?.id ?? s) === 'other')
    },
    // Keys used to determine whether the Compliance section as a whole should be highlighted
    complianceErrorKeys() {
      const keys = []
      this.questions.forEach((q) => {
        keys.push(`compliance.${q.key}.answer`, `compliance.${q.key}.remarks`)
      })
      return keys
    },
  },

  methods: {
    fieldError(key) {
      const value = this.errors ? this.errors[key] : null
      if (!value) return ''
      return Array.isArray(value) ? value[0] : value
    },

    sectionHasError(keys) {
      return keys.some((k) => !!this.fieldError(k))
    },

    emptyMaterialDraft() {
      return {
        id: null,
        name: '',
        type: '',
        packaging_size: '',
        packaging_type: '',
        lead_time: '',
        sub_ingredients: '',
        allergens: [],
        files: [],
      }
    },
    emptyProductDraft() {
      return {
        id: null,
        name: '',
        application: '',
        sku: '',
        production_type: '',
        unit: '',
        description: '',
      }
    },

    formatSlug(str) {
      if (!str || typeof str !== 'string') return ''
      return str
        .split('_')
        .filter(Boolean)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ')
    },
    productionTypeLabel(value) {
      if (value === 'wip') return 'WIP (Work In Progress)'
      if (value === 'fp') return 'FP (Finish Product)'
      return '—'
    },

    async loadQues() {
      this.loadingConfig = true
      try {
        const result = await form_api_submit({
          url: 'supplier/create_supplier',
          method: 'GET',
          show_success_toast: false,
          show_error_toast: false,
        })

        if (result?.success) {
          const data = result.data || {}
          this.questions = data.questions || []
          this.ipCerts = data.ipCerts || []
          this.nonCertQuestions = data.nonCertQuestions || []
          this.allergensOptions = data.allergens || []
          this.serviceOptions = [...(data.services || []), { id: 'other', name: 'Other' }]
          this.questions.forEach((q) => {
            this.compliance[q.key] = { answer: '', remarks: '' }
          })
          this.ipCerts.forEach((c) => {
            this.ipCertsForm[c.key] = { selected: false, file: null }
          })
          this.nonCertQuestions.forEach((q) => {
            this.nonCert[q.key] = { answer: '', details: '', second_detail: '', remarks: '' }
          })
        } else {
          this.topError = 'Could not load the form. Please refresh and try again.'
        }
      } catch (error) {
        this.topError = 'Could not load the form. Please refresh and try again.'
      } finally {
        this.loadingConfig = false
      }
    },

    ynClass(opt) {
      if (opt === 'YES') return 'yn-yes'
      if (opt === 'NO') return 'yn-no'
      return 'yn-na'
    },
    ynIcon(opt) {
      if (opt === 'YES') return '✓'
      if (opt === 'NO') return '✕'
      return '—'
    },

    onSimpleFile(e, key) {
      this.form[key] = e.target.files[0] || null
    },

    async checkEmail() {
      this.emailMessage = ''
      this.emailMessageIsError = false
      const email = this.form.email
      if (!email) return
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(email)) {
        this.emailMessage = 'Invalid email format'
        this.emailMessageIsError = true
        return
      }

      try {
        const result = await form_api_submit({
          url: this.checkEmailUrl,
          method: 'POST',
          data: { email },
          show_success_toast: false,
          show_error_toast: false,
        })

        if (result?.success && result.data?.statuses?.email_existence !== 'no') {
          this.emailMessage = 'Your information is already on file — some fields have been filled in.'
          this.emailMessageIsError = false
          this.prefillFromLookup(result.data.user)
        } else {
          this.emailMessage = 'Email is available'
          this.emailMessageIsError = false
          this.lockedFromLookup = false
        }
      } catch (error) {
        console.error('Email check failed:', error)
      }
    },

    prefillFromLookup(user) {
      if (!user) return
      this.lockedFromLookup = true
      this.form.company_name = user.company_name ?? this.form.company_name
      this.form.phone = user.phone ?? this.form.phone
      this.form.physical_address = user.street_address ?? this.form.physical_address
      this.form.street_city = user.street_city ?? this.form.street_city
      this.form.state = user.state ?? this.form.state
      this.form.postal_code = user.postal_code ?? this.form.postal_code
      this.form.poc = [user.first_name, user.last_name].filter(Boolean).join(' ')
      this.form.title = user.title ?? this.form.title
      this.form.qa_name = user.qa_name ?? this.form.qa_name
      this.form.qa_title = user.qa_title ?? this.form.qa_title
      this.form.qa_email = user.qa_email ?? this.form.qa_email
      this.form.qa_phone = user.qa_phone ?? this.form.qa_phone
    },

    addIpExtra() {
      this.ipExtraCerts.push({ id: `extra_${this.ipExtraCounter++}`, title: '', file: null })
    },
    removeIpExtra(idx) {
      this.ipExtraCerts.splice(idx, 1)
    },

    certificateKey(cert) {
      return cert.isOther ? cert.id : cert.value.replace(/[^a-zA-Z0-9]/g, '_')
    },
    addCertificate() {
      const value = this.certToAdd
      if (!value) return
      const isOther = value === '__other__'
      if (!isOther && this.addedCertValues.has(value)) {
        this.certToAdd = ''
        return
      }
      this.selectedCertificates.push({
        id: `cert_${this.certCounter++}`,
        value,
        isOther,
        title: '',
        file: null,
        expiryDate: '',
        comment: '',
      })
      if (!isOther) this.addedCertValues.add(value)
      this.certToAdd = ''
    },
    removeCertificate(idx) {
      const cert = this.selectedCertificates[idx]
      if (cert && !cert.isOther) this.addedCertValues.delete(cert.value)
      this.selectedCertificates.splice(idx, 1)
    },

    // ---- Materials: add-to-list / edit-in-place flow ----
    addOrUpdateMaterial() {
      this.materialDraftError = ''
      if (!this.materialDraft.name || !this.materialDraft.type) {
        this.materialDraftError = 'Name and Type are required.'
        return
      }
      if (this.editingMaterialIndex !== null) {
        this.materials.splice(this.editingMaterialIndex, 1, { ...this.materialDraft })
        this.editingMaterialIndex = null
      } else {
        this.materials.push({ ...this.materialDraft, id: `material_${this.materialCounter++}` })
      }
      this.materialDraft = this.emptyMaterialDraft()
    },
    editMaterial(idx) {
      this.editingMaterialIndex = idx
      this.materialDraft = { ...this.materials[idx] }
      this.materialDraftError = ''
    },
    cancelMaterialEdit() {
      this.editingMaterialIndex = null
      this.materialDraft = this.emptyMaterialDraft()
      this.materialDraftError = ''
    },
    removeMaterial(idx) {
      this.materials.splice(idx, 1)
      if (this.editingMaterialIndex === idx) this.cancelMaterialEdit()
    },

    // ---- Products: add-to-list / edit-in-place flow ----
    addOrUpdateProduct() {
      this.productDraftError = ''
      if (!this.productDraft.name || !this.productDraft.production_type || !this.productDraft.unit) {
        this.productDraftError = 'Name, Production Type, and Unit are required.'
        return
      }
      if (this.editingProductIndex !== null) {
        this.products.splice(this.editingProductIndex, 1, { ...this.productDraft })
        this.editingProductIndex = null
      } else {
        this.products.push({ ...this.productDraft, id: `product_${this.productCounter++}` })
      }
      this.productDraft = this.emptyProductDraft()
    },
    editProduct(idx) {
      this.editingProductIndex = idx
      this.productDraft = { ...this.products[idx] }
      this.productDraftError = ''
    },
    cancelProductEdit() {
      this.editingProductIndex = null
      this.productDraft = this.emptyProductDraft()
      this.productDraftError = ''
    },
    removeProduct(idx) {
      this.products.splice(idx, 1)
      if (this.editingProductIndex === idx) this.cancelProductEdit()
    },

    buildFormData() {
      const fd = new FormData()

      // ---------- All top-level company / POC / QA fields ----------
      Object.entries(this.form).forEach(([key, value]) => {
        if (value === null || value === undefined) return
        fd.append(key, value instanceof File ? value : String(value))
      })
      const primaryType = this.businessTypes.includes('manufacturer')
        ? 'fp_manufacturer'
        : this.businessTypes.includes('supplier')
          ? 'supplier'
          : ''
      fd.append('type', primaryType)
      this.businessTypes.forEach((t) => fd.append('types[]', t))

      // ---------- Regulatory compliance questionnaire ----------
      Object.entries(this.compliance).forEach(([key, entry]) => {
        fd.append(`compliance[${key}][answer]`, entry.answer)
        fd.append(`compliance[${key}][remarks]`, entry.remarks)
      })

      // ---------- Identity Preserved Certifications ----------
      fd.append('ip_cert_option', this.ipCertOption)
      if (this.ipCertOption === 'yes') {
        Object.entries(this.ipCertsForm).forEach(([key, entry]) => {
          fd.append(`ip_certs[${key}][selected]`, entry.selected ? '1' : '')
          if (entry.file) fd.append(`ip_certs[${key}][file]`, entry.file)
        })
        this.ipExtraCerts.forEach((extra) => {
          fd.append(`ip_certs[${extra.id}][title]`, extra.title)
          if (extra.file) fd.append(`ip_certs[${extra.id}][file]`, extra.file)
        })
      }

      // ---------- Food safety certification / non-cert questionnaire ----------
      fd.append('foodSafetyCert', this.foodSafetyCert)

      if (this.foodSafetyCert === 'yes') {
        this.selectedCertificates.forEach((cert) => {
          const key = this.certificateKey(cert)
          fd.append(`certificates[${key}][title]`, cert.isOther ? cert.title : cert.value)
          if (cert.file) fd.append(`certificates[${key}][file]`, cert.file)
          fd.append(`certificates[${key}][expiry_date]`, cert.expiryDate)
          fd.append(`certificates[${key}][comment]`, cert.comment)
        })
      } else if (this.foodSafetyCert === 'no') {
        Object.entries(this.nonCert).forEach(([key, entry]) => {
          fd.append(`non_cert[${key}][answer]`, entry.answer)
          fd.append(`non_cert[${key}][details]`, entry.details)
          fd.append(`non_cert[${key}][second_detail]`, entry.second_detail)
          fd.append(`non_cert[${key}][remarks]`, entry.remarks)
        })
      }

      // ---------- Materials ----------
      this.materials.forEach((material) => {
        const key = material.id
        fd.append(`materials[${key}][name]`, material.name)
        fd.append(`materials[${key}][type]`, material.type)
        fd.append(`materials[${key}][packaging_size]`, material.packaging_size)
        fd.append(`materials[${key}][packaging_type]`, material.packaging_type)
        fd.append(`materials[${key}][lead_time]`, material.lead_time)
        fd.append(`materials[${key}][sub_ingredients]`, material.sub_ingredients)
        material.allergens.forEach((allergen) => {
          const allergenId = allergen && typeof allergen === 'object' ? allergen.id : allergen
          fd.append(`materials[${key}][allergen_ids][]`, allergenId)
        })
        material.files.forEach((file, fIdx) => {
          fd.append(`materials[${key}][files][${fIdx}]`, file)
        })
      })

      // ---------- Services ----------
      this.servicesSelected.forEach((service) => {
        const id = service && typeof service === 'object' ? service.id : service
        fd.append('service_ids[]', id)
      })
      if (this.hasOtherServiceSelected) {
        fd.append('services_other_label', this.servicesOtherLabel)
      }

      // ---------- Products ----------
      this.products.forEach((product) => {
        const key = product.id
        fd.append(`products[${key}][name]`, product.name)
        fd.append(`products[${key}][application]`, product.application)
        fd.append(`products[${key}][sku]`, product.sku)
        fd.append(`products[${key}][production_type]`, product.production_type)
        fd.append(`products[${key}][unit]`, product.unit)
        fd.append(`products[${key}][description]`, product.description)
      })

      return fd
    },
    toRawFile(fileList) {
      return fileList[0] ? markRaw(fileList[0]) : null
    },
    toRawFiles(fileList) {
      return Array.from(fileList).map((f) => markRaw(f))
    },

    resetForm() {
      this.businessTypes = []
      this.form = {
        company_name: '',
        email: '',
        phone: '',
        manufacturing_type: '',
        product_brand_type: '',
        external_type: '',
        physical_address: '',
        street_city: '',
        state: '',
        postal_code: '',
        logo: null,
        emergency_contact: '',
        poc: '',
        title: '',
        qa_name: '',
        qa_title: '',
        qa_email: '',
        qa_phone: '',
        production_city: '',
        production_state: '',
        production_address: '',
        production_postcode: '',
        services_description: '',
        comments: '',
      }
      this.lockedFromLookup = false
      this.emailMessage = ''
      this.emailMessageIsError = false

      this.questions.forEach((q) => {
        this.compliance[q.key] = { answer: '', remarks: '' }
      })

      this.ipCertOption = 'na'
      this.ipCerts.forEach((c) => {
        this.ipCertsForm[c.key] = { selected: false, file: null }
      })
      this.ipExtraCerts = []

      this.foodSafetyCert = ''
      this.certToAdd = ''
      this.selectedCertificates = []
      this.addedCertValues = new Set()

      this.nonCertQuestions.forEach((q) => {
        this.nonCert[q.key] = { answer: '', details: '', second_detail: '', remarks: '' }
      })

      this.materials = []
      this.materialDraft = this.emptyMaterialDraft()
      this.editingMaterialIndex = null
      this.materialDraftError = ''

      this.products = []
      this.productDraft = this.emptyProductDraft()
      this.editingProductIndex = null
      this.productDraftError = ''

      this.servicesSelected = []
      this.servicesOtherLabel = ''
      this.errors = {}
    },

    validate() {
      this.errors = {}
      let isValid = true

      if (!this.businessTypes.length) {
        this.errors.businessTypes = 'Select at least one option.'
        isValid = false
      }

      // ---- Regulatory Compliance Questionnaire ----
      this.questions.forEach((q) => {
        const entry = this.compliance[q.key]
        if (!entry.answer) {
          this.errors[`compliance.${q.key}.answer`] = 'Please answer this question.'
          isValid = false
          return
        }
        if (q.has_remarks === 'yes' && entry.answer === 'NO' && !entry.remarks.trim()) {
          this.errors[`compliance.${q.key}.remarks`] = 'Remarks are required when answering NO.'
          isValid = false
        }
      })

      if (!this.foodSafetyCert) {
        this.errors.foodSafetyCert = 'Please select Yes or No.'
        isValid = false
      }

      // ---- Food Safety Management Certification ----
      if (this.foodSafetyCert === 'yes') {
        if (!this.selectedCertificates.length) {
          this.errors.certificates = 'Please add at least one certificate.'
          isValid = false
        }
        this.selectedCertificates.forEach((cert) => {
          const key = this.certificateKey(cert)
          if (cert.isOther && !cert.title.trim()) {
            this.errors[`certificates.${key}.title`] = 'Certificate title is required.'
            isValid = false
          }
          if (!cert.file) {
            this.errors[`certificates.${key}.file`] = 'Certificate file is required.'
            isValid = false
          }
          if (!cert.expiryDate) {
            this.errors[`certificates.${key}.expiryDate`] = 'Certificate expiry date is required.'
            isValid = false
          }
        })
      }

      if (this.foodSafetyCert === 'no') {
        this.nonCertQuestions.forEach((q) => {
          const entry = this.nonCert[q.key]
          if (!entry.answer) {
            this.errors[`non_cert.${q.key}.answer`] = 'Please answer this question.'
            isValid = false
            return
          }
          if (q.has_remarks && entry.answer === 'NO' && (q.remarks_required ?? true) && !entry.remarks.trim()) {
            this.errors[`non_cert.${q.key}.remarks`] = q.remarks_placeholder || 'Remarks are required when answering NO.'
            isValid = false
          }
        })
      }

      this.materials.forEach((material) => {
        if (!material.name || !material.type) {
          this.errors[`materials.${material.id}`] = 'Name and type are required for each material.'
          isValid = false
        }
      })
      if (this.businessTypes.includes('supplier') && !this.materials.length) {
        this.errors.materials = 'Please add at least one material.'
        isValid = false
      }

      if (this.businessTypes.includes('service_provider')) {
        if (this.hasOtherServiceSelected && !this.servicesOtherLabel.trim()) {
          this.errors['services.other_label'] = 'Please specify the "Other" service.'
          isValid = false
        }
      }

      this.products.forEach((product) => {
        if (!product.name || !product.production_type || !product.unit) {
          this.errors[`products.${product.id}`] = 'Name, Production Type, and Unit are required for each product.'
          isValid = false
        }
      })
      if (this.businessTypes.includes('manufacturer') && !this.products.length) {
        this.errors.products = 'Please add at least one product.'
        isValid = false
      }

      return isValid
    },

    scrollToFirstError() {
      this.$nextTick(() => {
        const el = document.querySelector('.is-invalid, .rform__error, .q-row--error, .section-block--has-error')
        el?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      })
    },

    async handleSubmit() {
      if (!this.validate()) {
        this.topError = 'Please fix the highlighted fields before submitting.'
        this.scrollToFirstError()
        return
      }
      this.submitting = true
      this.errors = {}
      this.topError = ''

      const result = await form_api_submit({
        url: 'supplier/signup',
        method: 'POST',
        data: this.buildFormData(),
        curr_form_id: '#supplier_signup_form',
        show_success_toast: true,
        show_error_toast: true,
        remove_pre_toasts: true,
      })

      if (result?.success) {
        const { token } = result.data || {}
        if (token) localStorage.setItem('supplier_token', token)
        localStorage.setItem('supplier_id', result.data.supplier_id)
        localStorage.setItem('company_id', result.data.company_id)
        this.isLoggedIn = true
        this.$router.push({ name: 'Profile' })
        this.resetForm()
        this.$emit('submitted', result.data)
      } else if (result?.data?.error_type === 'validation') {
        this.errors = result.data.errors
        this.topError = 'Please fix the highlighted fields before submitting.'
        this.scrollToFirstError()
      }

      this.submitting = false
    },
  },
}
</script>

<style scoped>
.hero {
  max-width: 640px;
  margin: 0 auto 36px;
  text-align: center;
}
</style>